import { useState } from "react";
import { Mail, Send } from "lucide-react";

/* Icone social ufficiali a colori */
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <defs>
      <linearGradient id="ig-grad" x1="0" y1="1" x2="1" y2="0">
        <stop offset="0" stopColor="#FEDA75" />
        <stop offset="0.45" stopColor="#FA7E1E" />
        <stop offset="0.75" stopColor="#D62976" />
        <stop offset="1" stopColor="#962FBF" />
      </linearGradient>
    </defs>
    <rect x="1.5" y="1.5" width="21" height="21" rx="5.5" fill="url(#ig-grad)" />
    <circle cx="12" cy="12" r="4.4" fill="none" stroke="#fff" strokeWidth="1.7" />
    <circle cx="17.2" cy="6.8" r="1.3" fill="#fff" />
  </svg>
);
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <circle cx="12" cy="12" r="10.5" fill="#1877F2" />
    <path d="M13.4 20.5v-6.8h2.3l.4-2.8h-2.7V9.1c0-.8.3-1.4 1.5-1.4h1.3V5.2c-.3 0-1.1-.1-2-.1-2 0-3.4 1.2-3.4 3.5v2.3H8.5v2.8h2.3v6.8h2.6z" fill="#fff" />
  </svg>
);
const LinkedinIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <rect x="1.5" y="1.5" width="21" height="21" rx="3.5" fill="#0A66C2" />
    <path d="M7.1 9.6H4.6V19h2.5V9.6zM5.8 8.3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM19.4 19h-2.5v-4.5c0-1.1-.4-1.9-1.4-1.9-.7 0-1.1.5-1.3 1-.1.2-.1.5-.1.8V19h-2.5V9.6h2.5v1.2c.3-.5.9-1.3 2.3-1.3 1.7 0 3 1.1 3 3.6V19z" fill="#fff" />
  </svg>
);
const WikipediaIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <circle cx="12" cy="12" r="10.5" fill="#fff" />
    <text x="12" y="16.6" textAnchor="middle" fontFamily="Georgia, 'Times New Roman', serif" fontSize="12.5" fontWeight="700" fill="#202122">W</text>
  </svg>
);
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";

const EMPTY = { name: "", email: "", organization: "", subject: "", message: "" };

const inputCls =
  "w-full rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-amber-400/70";

export default function Contatti() {
  const { t } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success(t("contatti.toastSuccess"));
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contatti" className="py-14 sm:py-32 bg-[#0B101B] relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(56,189,248,0.06),transparent_55%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal x={-40} y={0}>
            <span className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400">{t("contatti.eyebrow")}</span>
            <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[0.95]">
              {t("contatti.titleA")}<br /><span className="text-amber-400">{t("contatti.titleAccent")}</span>
            </h2>
            <p className="mt-6 text-sm sm:text-base text-slate-400 leading-relaxed max-w-md">
              {t("contatti.subtitle")}
            </p>
            <div className="mt-9 space-y-4">
              <a href="mailto:giacomo.bertagnolli@live.it" data-testid="contact-email-link" className="flex items-center gap-4 text-slate-200 hover:text-amber-400 transition-colors">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-700"><Mail className="h-4 w-4" /></span>
                <span className="text-sm sm:text-base">giacomo.bertagnolli@live.it</span>
              </a>
              <div className="flex items-center gap-4">
                <a href="https://instagram.com/jack_bertagnolli" target="_blank" rel="noreferrer" data-testid="contact-social-instagram" aria-label="Instagram"
                   className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 transition-all duration-300 hover:border-amber-400/60 hover:scale-105">
                  <InstagramIcon />
                </a>
                <a href="https://www.facebook.com/profile.php?id=100007969216308" target="_blank" rel="noreferrer" data-testid="contact-social-facebook" aria-label="Facebook"
                   className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 transition-all duration-300 hover:border-amber-400/60 hover:scale-105">
                  <FacebookIcon />
                </a>
                <a href="https://www.linkedin.com/in/giacomo-bertagnolli" target="_blank" rel="noreferrer" data-testid="contact-social-linkedin" aria-label="LinkedIn"
                   className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 transition-all duration-300 hover:border-amber-400/60 hover:scale-105">
                  <LinkedinIcon />
                </a>
                <a href="https://it.wikipedia.org/wiki/Giacomo_Bertagnolli" target="_blank" rel="noreferrer" data-testid="contact-social-wikipedia" aria-label="Wikipedia" title="La mia pagina Wikipedia"
                   className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 transition-all duration-300 hover:border-amber-400/60 hover:scale-105">
                  <WikipediaIcon />
                </a>
                <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("contatti.follow")}</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15} x={40} y={0}>
            <form data-testid="contact-form" onSubmit={submit} className="rounded-2xl border border-slate-800/80 bg-slate-900/50 backdrop-blur-md p-6 sm:p-8 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="contact-input-name" required placeholder={t("contatti.namePh")} value={form.name} onChange={set("name")} className={inputCls} />
                <input data-testid="contact-input-email" required type="email" placeholder={t("contatti.emailPh")} value={form.email} onChange={set("email")} className={inputCls} />
              </div>
              <input placeholder={t("contatti.orgPh")} value={form.organization} onChange={set("organization")} className={inputCls} />
              <input data-testid="contact-input-subject" placeholder={t("contatti.subjectPh")} value={form.subject} onChange={set("subject")} className={inputCls} />
              <textarea data-testid="contact-input-message" required rows={5} placeholder={t("contatti.messagePh")} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-amber-400 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-[#090D14] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
              >
                {sending ? t("contatti.sending") : t("contatti.submit")}
                <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
